"use client";

import { useEffect, useRef } from "react";
import type { DotLottieWC } from "@lottiefiles/dotlottie-wc";

interface LottiePlayerProps {
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  className?: string;
  minDevicePixelRatio?: number;
  onReady?: () => void;
}

export function LottiePlayer({
  src,
  autoplay = true,
  loop = true,
  className,
  minDevicePixelRatio,
  onReady,
}: LottiePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<DotLottieWC | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Dynamically import to avoid SSR issues
    import("@lottiefiles/dotlottie-wc").then((module) => {
      if (!container || playerRef.current) return;

      const player = new module.DotLottieWC();
      if (minDevicePixelRatio) {
        player.renderConfig = {
          autoResize: true,
          devicePixelRatio: Math.max(
            window.devicePixelRatio,
            minDevicePixelRatio,
          ),
          quality: 100,
        };
      }
      player.src = src;
      player.autoplay = autoplay;
      player.loop = loop;
      player.style.width = "100%";
      player.style.height = "100%";

      container.appendChild(player);
      playerRef.current = player;
      onReady?.();
    });

    return () => {
      if (playerRef.current && container) {
        container.removeChild(playerRef.current);
        playerRef.current = null;
      }
    };
  }, [src, autoplay, loop, minDevicePixelRatio, onReady]);

  return <div ref={containerRef} className={className} />;
}
